/// <amd-module name="Wit/Common/KanbanBoardSettings/Components/BoardSettingsViews/SwimlanesSettings" />
import { IObservableArray, IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { BoardRow } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { BoardData } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import { divyCardRules } from "Wit/BoardsHub/Common/StylesUtils";
import { ISwimlanesValidator, SwimlanesValidator } from "Wit/BoardsHub/Common/SwimlanesValidator";
import { getSwimlaneRulesFromRules, ISwimlaneRules } from "Wit/BoardsHub/Common/SwimlaneUtils";
import * as Resources from "Wit/Common/KanbanBoardSettings/Resources";

export type SwimlaneRulesMap = {
    [key: string]: Readonly<ISwimlaneRules> | undefined;
};

export interface ISwimlanesSettings {
    readonly swimlanes: IReadonlyObservableArray<BoardRow>;
    readonly swimlanesRules: IReadonlyObservableValue<Readonly<SwimlaneRulesMap>>;
    readonly originalSwimlaneRules: Readonly<SwimlaneRulesMap>;
    readonly isDirty: IReadonlyObservableValue<boolean>;
    readonly isValid: IReadonlyObservableValue<boolean>;
    getSwimlaneNameError(swimlaneId: string): IReadonlyObservableValue<string | undefined>;
    isDefault(swimlaneId: string): boolean;
    /**
     * Adds a new swimlane to the settings.
     * @returns the id of the newly-added swimlane.
     */
    addSwimlane(index: number): string;
    removeSwimlane(swimlaneId: string): void;
    setOrder(swimlaneId: string, index: number): void;
    setName(swimlaneId: string, name: string): void;
    setColor(swimlaneId: string, color: string | null): void;
    getRules(swimlaneId: string): ISwimlaneRules;
    setRules(swimlaneId: string, rules: ISwimlaneRules): void;
    setCriteriaErrorObservable(swimlaneId: string, errorObservable: IReadonlyObservableValue<string | undefined>): void;
}

export type SwimlaneSettingsBoardData = {
    boardModel: {
        boardSettings: {
            rows: BoardData["boardModel"]["boardSettings"]["rows"];
        };
        boardCardSettings: {
            styles: BoardData["boardModel"]["boardCardSettings"]["styles"];
        };
    };
};

export function makeSwimlanesSettings(boardData: SwimlaneSettingsBoardData): ISwimlanesSettings {
    return new SwimlanesSettings(boardData);
}

const DefaultRowId = "00000000-0000-0000-0000-000000000000";

class SwimlanesSettings implements ISwimlanesSettings {
    private _originalSwimlanes: BoardRow[];
    private _swimlanes: IObservableArray<BoardRow>;
    private _validator: ISwimlanesValidator;
    private _originalSwimlaneRules: SwimlaneRulesMap;
    private _swimlaneRules: ObservableValue<SwimlaneRulesMap>;
    private _criteriaErrorObservables: Map<string, IReadonlyObservableValue<string | undefined> | undefined> = new Map();
    private _isValid = new ObservableValue<boolean>(true);

    public isDirty = new ObservableValue(false);

    public get isValid() {
        return this._isValid;
    }

    public get originalSwimlaneRules() {
        return this._originalSwimlaneRules;
    }

    constructor(boardData: SwimlaneSettingsBoardData) {
        this._originalSwimlanes = boardData.boardModel.boardSettings.rows;
        this._swimlanes = new ObservableArray(this._originalSwimlanes.map(swimlane => ({ ...swimlane })));

        const { swimlaneRules } = divyCardRules(boardData.boardModel.boardCardSettings.styles);

        this._originalSwimlaneRules = {};
        getSwimlaneRulesFromRules(swimlaneRules).forEach(rule => {
            this._originalSwimlaneRules[rule.rowId] = rule;
        });
        this._swimlaneRules = new ObservableValue({ ...this._originalSwimlaneRules });

        this._validator = new SwimlanesValidator();
        this.validate();
    }

    public get swimlanesRules() {
        return this._swimlaneRules;
    }

    public get swimlanes() {
        return this._swimlanes;
    }

    public setRules(swimlaneId: string, rules: ISwimlaneRules): void {
        if (rules.filterClauses.length > 0) {
            this._swimlaneRules.value = { ...this._swimlaneRules.value, [swimlaneId]: rules };
        } else {
            this._swimlaneRules.value = { ...this._swimlaneRules.value, [swimlaneId]: undefined };
        }
        this.checkDirty();
    }

    public getRules(swimlaneId: string): ISwimlaneRules {
        return this._swimlaneRules.value[swimlaneId] ?? { rowId: swimlaneId, filterClauses: [] };
    }

    public isDefault(swimlaneId: string): boolean {
        // The default swimlane has an empty guid for an id.
        return swimlaneId === DefaultRowId;
    }

    public addSwimlane(index: number): string {
        const { id, name } = this.getNewColumnIdAndName();
        const newSwimlane: BoardRow = {
            id,
            name,
            isDeleted: false,
            order: index,
            isDefault: false,
            color: ""
        };
        this._swimlanes.splice(index, 0, newSwimlane);
        this.checkDirty();
        return id;
    }

    public removeSwimlane(swimlaneId: string): void {
        this._swimlanes.value = this._swimlanes.value.filter(sl => sl.id !== swimlaneId);
        this.checkDirty();
        this.validate();
    }

    public setOrder(swimlaneId: string, index: number): void {
        const swimlane = this._swimlanes.value.find(swimlane => swimlane.id === swimlaneId);
        if (!swimlane) {
            return;
        }

        const newSwimlanes = this._swimlanes.value.filter(swimlane => swimlane.id !== swimlaneId);
        newSwimlanes.splice(index, 0, swimlane);

        this._swimlanes.value = newSwimlanes;
        this.checkDirty();
    }

    public setName(swimlaneId: string, name: string): void {
        this.setStringValue(swimlaneId, { name });
    }

    public setColor(swimlaneId: string, color: string): void {
        this.setStringValue(swimlaneId, { color });
    }

    private setStringValue(swimlaneId: string, stringValue: { [value: string]: string | null }): void {
        const index = this._swimlanes.value.findIndex(swimlane => swimlane.id === swimlaneId);

        if (index > -1) {
            const swimlane = { ...this._swimlanes.value[index], ...stringValue };
            this._swimlanes.splice(index, 1, swimlane);
            this.checkDirty();
            this.validate();
        }
    }

    public setCriteriaErrorObservable(swimlaneId: string, errorObservable: IReadonlyObservableValue<string | undefined>): void {
        const currentErrorObservable = this._criteriaErrorObservables.get(swimlaneId);
        if (currentErrorObservable) {
            currentErrorObservable.unsubscribe(this.onCriteriaErrorChanged);
        }
        this._criteriaErrorObservables.set(swimlaneId, errorObservable);
        errorObservable.subscribe(this.onCriteriaErrorChanged);
    }

    private onCriteriaErrorChanged = (): void => {
        this.validate();
    };

    private checkDirty(): void {
        this.setDirty(this.calculateIsDirty());
    }

    private calculateIsDirty(): boolean {
        if (this._originalSwimlanes.length !== this._swimlanes.length) {
            return true;
        }

        for (let i = 0; i < this._originalSwimlanes.length; ++i) {
            const originalSwimlane = this._originalSwimlanes[i];
            const currentSwimlane = this._swimlanes.value[i];
            const { id: originalId, name: originalName = "", color: originalColor = "" } = originalSwimlane;
            const { id: currentId, name: currentName = "", color: currentColor = "" } = currentSwimlane;

            if (originalId !== currentId || originalName !== currentName || originalColor !== currentColor) {
                return true;
            }
        }

        if (Object.keys(this._originalSwimlaneRules).length !== Object.keys(this._swimlaneRules.value).length) {
            return true;
        }

        for (const [swimlaneId, originalRules] of Object.entries(this._originalSwimlaneRules)) {
            const currentRules = this._swimlaneRules.value[swimlaneId];
            if (!currentRules && !originalRules) {
                continue;
            }
            if (!currentRules || !originalRules) {
                return true;
            }

            if (originalRules.filterClauses.length !== currentRules.filterClauses.length) {
                return true;
            }

            for (let i = 0; i < originalRules.filterClauses.length; ++i) {
                const originalRule = originalRules.filterClauses[i];
                const currentRule = currentRules.filterClauses[i];

                if (
                    originalRule.fieldName !== currentRule.fieldName ||
                    originalRule.operator !== currentRule.operator ||
                    originalRule.value !== currentRule.value
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Simple wrapper to make sure we don't unnecessarily update the observable.
     */
    private setDirty(dirty: boolean): void {
        if (this.isDirty.value !== dirty) {
            this.isDirty.value = dirty;
        }
    }

    public getSwimlaneNameError(swimlaneId: string): IReadonlyObservableValue<string | undefined> {
        return this._validator.getSwimlaneNameError(swimlaneId);
    }

    /**
     * Returns a new column name (a string like "New Swimlane" or "New Swimlane 1")
     * and a temporary id (a string like "temp-id" or "temp-id-1"). These will
     * not match the title or id of any existing columns.
     * @returns
     */
    private getNewColumnIdAndName(): { id: string; name: string } {
        let id = "temp-id";
        let idSuffix = 0;
        while (this._swimlanes.value.map(sl => sl.id).includes(id)) {
            id = `temp-id-${++idSuffix}`;
        }

        let name = Resources.DefaultNewSwimlaneName;
        let nameSuffix = 0;
        while (this._swimlanes.value.map(sl => sl.name).includes(name)) {
            name = format(Resources.IndexedNewSwimlaneName, ++nameSuffix);
        }

        return { id, name };
    }

    private validate() {
        this._validator.validate(this._swimlanes.value);
        for (const [_, criteriaErrorObservable] of this._criteriaErrorObservables) {
            if (criteriaErrorObservable && criteriaErrorObservable.value !== undefined) {
                this._isValid.value = false;
                return;
            }
        }
        this._isValid.value = this._validator.isValid.value;
    }
}
